import { useEffect, useLayoutEffect } from 'react'
import mapboxgl, { MapMouseEvent, SymbolLayerSpecification } from 'mapbox-gl'

import { MAP_CONTROL_LAYER_IDS } from './constants'
import { useMapContext } from './MapContext'
import { isMapControlClick } from './mapControlClick'
import { hasLiveStyle, safeAddLayer, safeRemoveLayer } from './mapTeardown'

interface MapSymbolLayerProps {
  id: string
  sourceId: string
  imageSrc?: string
  icon?: string | mapboxgl.ExpressionSpecification
  layout?: SymbolLayerSpecification['layout']
  paint?: SymbolLayerSpecification['paint']
  filter?: any[]
  iconColor?: string
  textColor?: string
  beforeLayerId?: string
  minZoomLevel?: number
  onSymbolClick?: (event: MapMouseEvent) => void
}

function MapSymbolLayer(props: MapSymbolLayerProps): null {
  const {
    id,
    sourceId,
    imageSrc,
    icon,
    layout,
    paint,
    filter,
    iconColor,
    textColor,
    minZoomLevel,
    beforeLayerId,
    onSymbolClick
  } = props

  const { map } = useMapContext()

  const imageName = imageSrc ? imageSrc.split('/').pop() : null

  useLayoutEffect(() => {
    if (!map) return

    if (imageSrc && imageName) {
      map.loadImage(imageSrc, (error, image) => {
        if (error) throw error
        if (!image) throw 'An error occurred while loading the image'

        // The style may have been reloaded while the image was loading.
        if (!hasLiveStyle(map)) return
        if (!map.hasImage(imageName)) {
          map.addImage(imageName, image)
        }
      })
    }

    // Text-only symbol layers (e.g. geometry labels) pass neither `imageSrc`
    // nor `icon`. Mapbox rejects both `undefined` and `null` for `icon-image`,
    // so the key must be omitted entirely when there is no image to resolve.
    const iconImage = imageName ?? icon

    safeAddLayer(
      map,
      {
        id,
        type: 'symbol',
        source: sourceId,
        filter: filter ?? ['all'],
        layout: {
          ...layout,
          ...(iconImage ? { 'icon-image': iconImage } : {})
        },
        paint: {
          'text-color': textColor ?? paint?.['text-color'] ?? 'transparent',
          'icon-color': iconColor ?? paint?.['icon-color'] ?? 'transparent',
          ...paint
        },
        minzoom: minZoomLevel ?? 0
      },
      beforeLayerId
    )

    const symbolClickListener = (event: MapMouseEvent) => {
      if (!MAP_CONTROL_LAYER_IDS.includes(id) && isMapControlClick(map, event.point)) return
      onSymbolClick?.(event)
    }

    if (onSymbolClick) {
      map.on('click', id, symbolClickListener)
    }

    const mouseEnterListener = () => {
      map.getCanvas().style.cursor = 'pointer'
    }

    const mouseLeaveListener = () => {
      map.getCanvas().style.cursor = ''
    }

    if (onSymbolClick) {
      map.on('mouseenter', id, mouseEnterListener)
      map.on('mouseleave', id, mouseLeaveListener)
    }

    return () => {
      if (onSymbolClick) {
        map.off('click', id, symbolClickListener)
        map.off('mouseenter', id, mouseEnterListener)
        map.off('mouseleave', id, mouseLeaveListener)
      }

      map.getCanvas().style.cursor = ''

      safeRemoveLayer(map, id)
    }
  }, [map, filter, layout, paint, icon, imageSrc])

  return null
}

export { MapSymbolLayer }
